import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '../AuthContext';
import BotPresence from './BotPresence';
import { botPersonality } from '../botPersonality';
import { botTimelineTitle } from '../botActivityCopy';
import { activityMarketConsistent, botPresenceState, distinctActivityEvents } from '../botPresenceState';
import { verifiedExecution, verifiedReplay } from '../botActivityEvidence';
import { localFreshnessExpired, subscribeVisibleFreshness } from '../visibleFreshnessClock';
import { marketPresence } from '../marketPresence';
import { selectClosedBotMessage } from '../marketClosedPersonality';
import { selectPremarketActivity } from '../premarketActivities';

const formatTime = (value) => (value
  ? new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
  : null);
const formatStageTime = (value) => (value
  ? new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'medium' })
  : null);

const MAX_BOTS_PER_REQUEST = 25;
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
const validInstant = (value) => ISO_INSTANT.test(value) && Number.isFinite(Date.parse(value));

const withLocalFreshness = (response, requestElapsedMs) => {
  const asOf = response?.as_of;
  const staleAfter = response?.stale_after;
  if (response?.contract !== 'bot-live-activity-v1' || !Array.isArray(response.bots)
      || typeof asOf !== 'string' || typeof staleAfter !== 'string'
      || !ISO_INSTANT.test(asOf) || !ISO_INSTANT.test(staleAfter)) {
    throw new Error('Bot activity freshness authority is unavailable');
  }
  const serverWindow = Date.parse(staleAfter) - Date.parse(asOf);
  const duration = serverWindow - requestElapsedMs;
  if (!Number.isFinite(serverWindow) || serverWindow <= 0 || serverWindow > 45000
      || !Number.isFinite(duration) || duration <= 0) {
    throw new Error('Bot activity freshness authority is invalid');
  }
  const receivedAt = performance.now();
  return response.bots.map((bot) => ({
    ...bot,
    responseAsOf: asOf,
    localFreshness: { receivedAt, duration },
  }));
};

const fetchActivity = async (params) => {
  const startedAt = performance.now();
  const response = await axiosInstance.get('/api/bots/live-activity', { params });
  return withLocalFreshness(response.data, performance.now() - startedAt);
};

const progressLabel = {
  acquiring_data: 'Checking completed market data',
  checking_session: 'Market session checked',
  evaluating_symbols: 'Evaluating assigned instruments',
  recording_result: 'Recording verified results',
  completed: 'Strategy check completed',
  failed: 'Strategy check failed',
};

const timelineKind = (type) => {
  if (typeof type === 'string' && type.startsWith('MARKET_CHECK_')) return 'Strategy check';
  if (typeof type === 'string' && type.startsWith('PREPARATION_')) return 'Preparation';
  return 'Recorded activity';
};

const useVerifiedEvents = (botId, enabled) => useQuery({
  queryKey: ['bot-live-timeline', botId],
  queryFn: async () => (await axiosInstance.get('/api/activity', {
    params: { bot_id: botId, view: 'all', limit: 12 },
  })).data,
  enabled: enabled && botId != null,
  retry: 1,
  refetchInterval: 60000,
  refetchIntervalInBackground: false,
});

export const useBotLiveActivity = (enabled, botId = null, botIds = null) => useQuery({
  queryKey: ['bot-live-activity', botId, botIds],
  queryFn: async () => {
    if (botId != null) return { bots: await fetchActivity({ bot_id: botId }) };
    const bots = [];
    for (let index = 0; index < botIds.length; index += MAX_BOTS_PER_REQUEST) {
      const requested = botIds.slice(index, index + MAX_BOTS_PER_REQUEST);
      const page = await fetchActivity({ bot_ids: requested.join(',') });
      if (page.some((item) => !requested.includes(item.bot_id))) {
        throw new Error('Bot activity selection does not match the page');
      }
      bots.push(...page);
    }
    return { bots };
  },
  enabled: enabled && (botId != null || (botIds?.length || 0) > 0),
  retry: 1,
  refetchInterval: botId != null ? 15000 : (botIds?.length || 0) > 25 ? 30000 : 20000,
  refetchIntervalInBackground: false,
});

const formatMarketTime = (value) => {
  if (!value || !validInstant(value)) return null;
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  }).format(new Date(value));
};

export const BotLiveActivity = ({ bot, identityBot, query, compact = false }) => {
  const [, setClockTick] = useState(0);
  const [visible, setVisible] = useState(() => document.visibilityState === 'visible');
  useEffect(() => subscribeVisibleFreshness(
    document, window, () => setClockTick((value) => value + 1),
  ), []);
  useEffect(() => {
    const update = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);
  const activity = bot?.activity;
  const unavailable = query.isError || (query.data && !bot);
  const expired = localFreshnessExpired(bot?.localFreshness, performance.now());
  const running = activity?.code === 'evaluation_running'
    ? verifiedExecution(bot, performance.now()) : null;
  const safe = !unavailable && !expired && activityMarketConsistent(bot)
    && Boolean(activity?.code)
    && (activity.code !== 'evaluation_running' || running?.status === 'running');
  const presence = botPresenceState(bot, safe);
  const view = marketPresence(presence, bot);
  const openMarketMonitoring = safe && presence.kind === 'monitoring' && bot?.market?.phase === 'open';
  const openMarketWorking = safe && presence.kind === 'working' && bot?.market?.phase === 'open';
  const preMarketPreparing = safe && presence.kind === 'waking' && bot?.market?.phase === 'pre_open';
  const preparationActivity = safe && presence.kind === 'waking' ? selectPremarketActivity(bot) : null;
  const offHoursMessage = safe && presence.kind === 'sleeping'
    ? selectClosedBotMessage(bot, query.data?.bots) : null;
  const nextOpen = safe && view.state === 'sleeping' && bot?.market?.exchange_timezone
    ? formatMarketTime(bot.market.next_open) : null;
  return <section className={`bot-live-activity bot-market-presence ${compact ? 'bot-market-presence--compact' : ''}`}
    data-visual-state={view.state} data-motion={visible ? 'on' : 'off'}
    data-open-market-monitoring={openMarketMonitoring ? 'on' : 'off'}
    data-pre-market-preparing={preMarketPreparing ? 'on' : 'off'}
    aria-label="Bot activity">
    <div className="bot-market-presence__hero">
      <BotPresence kind={view.robot} motion={view.motion && (!['monitoring', 'working'].includes(presence.kind) || openMarketMonitoring || openMarketWorking)} personality={botPersonality(identityBot)} preparationActivity={preparationActivity}
        connected={safe && (view.state === 'watching' || view.state === 'waking') && activity?.worker_connected === true} />
      <div className="bot-market-presence__body">
        <div className="bot-market-presence__eyebrow"><span className="bot-market-presence__dot" aria-hidden="true" /><span className="bot-market-presence__eyebrow-label">Bot activity</span></div>
        <h3 className="bot-market-presence__title" role="status">{query.isPending && !query.data ? 'Checking bot status…' : view.title}</h3>
        <p className="bot-market-presence__summary">{view.summary}</p>
        {offHoursMessage && <p className="bot-market-presence__personality">{offHoursMessage}</p>}
        {nextOpen && <p className="bot-market-presence__schedule">Next market open: <time dateTime={bot.market.next_open}>{nextOpen}</time></p>}
        {view.messages.length > 0 && <div className="bot-market-presence__messages" aria-hidden="true">
          {view.messages.map((message) => <span key={message}>{message}</span>)}
        </div>}
      </div>
    </div>
    {view.line && (view.state !== 'waking' || preMarketPreparing) && <div className="bot-market-presence__track" aria-hidden="true"><span /></div>}
    {!safe && !query.isPending && <button type="button" onClick={() => query.refetch()} disabled={query.isFetching}
      className="bot-market-presence__retry">{query.isFetching ? 'Checking…' : 'Retry activity'}</button>}
  </section>;
};

export const BotRecentActivity = ({ botId, enabled }) => {
  const query = useVerifiedEvents(botId, enabled);
  const liveQuery = useBotLiveActivity(enabled, botId);
  const liveBot = liveQuery.data?.bots?.[0];
  const evidence = liveBot && !localFreshnessExpired(liveBot.localFreshness, performance.now())
    ? verifiedExecution(liveBot, performance.now()) : null;
  const execution = evidence?.status === 'running' ? evidence
    : evidence ? verifiedReplay(liveBot, performance.now()) : null;
  const events = distinctActivityEvents(query.data?.items)
    .filter((item) => validInstant(item.occurred_at));
  return <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900" aria-labelledby="bot-recent-activity-title">
    <h2 id="bot-recent-activity-title" className="text-xl font-black">Recorded activity · history</h2>
    <p className="mt-1 text-sm text-slate-500">Completed steps below happened at their recorded times; the animated market-watch status above describes the current bot state. Strategy steps follow their recorded order, and other Practice events show newest first. No broker orders are implied.</p>
    {execution && <div className="mt-4 rounded-xl border border-indigo-100 bg-indigo-50/70 p-3 dark:border-indigo-900 dark:bg-indigo-950/30" aria-label="Latest verified strategy steps">
      <p className="text-xs font-bold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">{execution.status === 'running' ? 'Current verified strategy check · in progress' : `Recorded strategy check · ${execution.status} at ${formatStageTime(execution.finished_at)}`}</p>
      <ol className="mt-2 space-y-2">{execution.events.map((item, index) => validInstant(item.at) && progressLabel[item.stage]
        ? <li key={`${execution.execution_id}:${item.at}:${index}`} className="text-sm text-slate-700 dark:text-slate-200"><time dateTime={item.at} className="font-semibold">{formatStageTime(item.at)}</time> · {progressLabel[item.stage]}{item.stage === 'evaluating_symbols' ? ` · ${item.completed_symbols} of ${execution.total_symbols}` : ''}</li>
        : null)}</ol>
    </div>}
    {query.isPending ? <p role="status" className="mt-4 text-sm">Loading recent events…</p>
      : query.isError ? <div role="alert" className="mt-4 text-sm text-amber-900 dark:text-amber-200">Recent events are unavailable. <button type="button" onClick={() => query.refetch()} disabled={query.isFetching} className="font-bold underline">{query.isFetching ? 'Retrying…' : 'Retry here'}</button></div>
        : !events.length ? <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">No recorded bot activity yet.</p>
          : <ol className="bot-activity-timeline mt-5 space-y-3 border-l-2 border-blue-100 pl-5 dark:border-blue-900">
            {events.map((item) => <li key={item.id} className="relative min-w-0 rounded-xl border border-slate-100 bg-slate-50/70 p-3 dark:border-slate-700 dark:bg-slate-800/60">
              <span aria-hidden="true" className="absolute -left-[1.7rem] top-4 h-2.5 w-2.5 rounded-full border-2 border-white bg-blue-500 dark:border-slate-900" />
              <div className="flex flex-wrap items-center justify-between gap-1">
                <span className="text-[11px] font-extrabold uppercase tracking-wide text-blue-700 dark:text-blue-300">{timelineKind(item.event_type)}</span>
                <time dateTime={item.occurred_at} className="text-xs text-slate-500 dark:text-slate-400">{formatTime(item.occurred_at)}</time>
              </div>
              <p className="mt-1 text-sm font-black">{botTimelineTitle(item)}</p>
              {item.detail && <p className="mt-1 text-sm leading-5 text-slate-600 dark:text-slate-300">{item.detail}</p>}
            </li>)}
          </ol>}
  </section>;
};
